import { useMemo } from "react";
import { useVarieties } from "./useVarieties";

const DEFAULT_COLOR = "#64748b"; // slate-500

/**
 * Builds a lookup map from varietal name/code → hex color.
 * Keys are lowercased for case-insensitive matching.
 * This is the single source of truth for variety colors across the app.
 */
export function useVarietyColorMap() {
  const { data: varieties } = useVarieties(true);

  const colorMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (!varieties) return map;
    for (const v of varieties) {
      if (!v.color) continue;
      // Index by lowercase name and code for flexible matching
      map[v.name.toLowerCase()] = v.color;
      map[v.code.toLowerCase()] = v.color;
    }
    return map;
  }, [varieties]);

  return colorMap;
}

/**
 * Resolve a varietal string to its hex color using the color map.
 * Tries exact match, then partial match.
 */
export function getVarietyColor(
  colorMap: Record<string, string>,
  varietal: string | null | undefined
): string {
  if (!varietal) return DEFAULT_COLOR;

  const key = varietal.toLowerCase().trim();

  // Exact match
  if (colorMap[key]) return colorMap[key];

  // Partial match (e.g., "Cabernet Sauvignon Clone 7" → "cabernet sauvignon")
  for (const [mapKey, color] of Object.entries(colorMap)) {
    if (key.includes(mapKey) || mapKey.includes(key)) return color;
  }

  return DEFAULT_COLOR;
}

/**
 * Builds a lookup map from variety code (lowercase) → full variety name.
 * Used in the tank planner to resolve the display name from the lot code's
 * VV segment, which is more reliable than the stored varietal text field
 * (which can become stale when a lot code is renamed).
 */
export function useVarietyNameMap(): Record<string, string> {
  const { data: varieties } = useVarieties(true);

  return useMemo(() => {
    const map: Record<string, string> = {};
    if (!varieties) return map;
    for (const v of varieties) {
      map[v.code.toLowerCase()] = v.name;
    }
    return map;
  }, [varieties]);
}

/** Default fallback color for unknown varietals */
export { DEFAULT_COLOR as VARIETY_DEFAULT_COLOR };
